import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  CircularProgress,
  Typography,
  Link,
  TextField,
  Box,
  Button,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { DataService } from "src/services/data-service";
import AddImagePopup from "./AddImage";

interface ImagePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selected: string | string[]) => void;
  multiSelect?: boolean;
}

const ImagePicker: React.FC<ImagePickerProps> = ({ open, onClose, onSelect, multiSelect = false }) => {
  const [images, setImages] = useState<{ id: string; name: string; url: string }[]>([]);
  const [filteredImages, setFilteredImages] = useState<typeof images>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [openAddImage, setOpenAddImage] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredImages(images);
    } else {
      const filtered = images.filter((img) =>
        img.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredImages(filtered);
    }
  }, [searchQuery, images]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await DataService.get("api/media/all");
      const data = await response.json();

      if (Array.isArray(data)) {
        const imageList = data.map((img) => ({ id: img.id, name: img.name, url: img.url }));
        setImages(imageList);
        setFilteredImages(imageList);
      } else {
        console.error("Unexpected API response format", data);
        setImages([]);
        setFilteredImages([]);
      }
    } catch (error) {
      console.error("Failed to fetch images", error);
      setImages([]);
      setFilteredImages([]);
    }
    setLoading(false);
  };

  // Toggle selection for multi-select
  const handleImageClick = (url: string) => {
    if (multiSelect) {
      const newSelected = new Set(selectedImages);
      if (newSelected.has(url)) {
        newSelected.delete(url);
      } else {
        newSelected.add(url);
      }
      setSelectedImages(newSelected);
    } else {
      onSelect(url);
      onClose();
    }
  };

  // Confirm multi-select selections
  const handleConfirmSelection = () => {
    onSelect(Array.from(selectedImages));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        {multiSelect ? "Select Images" : "Select an Image"}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ minHeight: "400px", display: "flex", flexDirection: "column" }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search images..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ marginBottom: "15px" }}
        />

        <Typography
          variant="body2"
          sx={{ marginBottom: "10px", textAlign: "right" }}
        >
          <Link
            component="button"
            onClick={() => setOpenAddImage(true)}
            sx={{ cursor: "pointer", fontWeight: "bold" }}
          >
            + Add New Image
          </Link>
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredImages.length === 0 ? (
          <Typography
            sx={{ textAlign: "center", width: "100%", padding: "20px" }}
          >
            No images available.
          </Typography>
        ) : (
          <Grid container spacing={2} justifyContent="center">
            {filteredImages.map((image) => {
              const isSelected = selectedImages.has(image.url);
              return (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Box
                    sx={{
                      textAlign: "center",
                      border: isSelected ? "2px solid blue" : "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: isSelected ? "#e0f7ff" : "#f9f9f9",
                      transition: "0.3s",
                      "&:hover": { backgroundColor: "#e0e0e0", cursor: "pointer" },
                    }}
                    onClick={() => handleImageClick(image.url)}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      style={{
                        width: "100%",
                        height: "180px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "5px",
                      }}
                    >
                      {image.name}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      {multiSelect && (
        <Box sx={{ p: 2, textAlign: "right" }}>
          <Button variant="contained" color="primary" onClick={handleConfirmSelection}>
            Confirm Selection
          </Button>
        </Box>
      )}

      <AddImagePopup
        open={openAddImage}
        onClose={() => setOpenAddImage(false)}
        onSuccess={() => {
          setOpenAddImage(false);
          fetchImages();
        }}
      />
    </Dialog>
  );
};

export default ImagePicker;
